import React, { useEffect, useState } from 'react';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Area, AreaChart
} from 'recharts';
import { 
  DollarSign, TrendingDown, Zap, Cloud, FileText, Loader2, 
  AlertTriangle, CheckCircle, Database, Cpu, RefreshCw,
  ChevronDown, ChevronUp, Eye
} from 'lucide-react';
import { api } from '../../utils/api';

// Color scheme for tiers
const TIER_COLORS: Record<string, string> = {
  pdfplumber: '#22c55e',      // Green - Free
  tesseract: '#84cc16',       // Lime - Free
  tesseract_validated: '#3b82f6', // Blue - Cheap
  paddleocr: '#14b8a6',       // Teal - Free
  csv_adapter: '#10b981',     // Emerald - Free
  excel_adapter: '#059669',   // Green - Free
  ofx_parser: '#047857',      // Dark Green - Free
  claude_ocr: '#8b5cf6',      // Purple - Paid
  textract: '#f59e0b',        // Amber - Paid
  unknown: '#6b7280',         // Gray
};

const TIER_LABELS: Record<string, string> = {
  pdfplumber: 'PDFPlumber (Free)',
  tesseract: 'Tesseract (Free)',
  tesseract_validated: 'Tesseract+Validation ($0.001)',
  paddleocr: 'PaddleOCR (Free)',
  csv_adapter: 'CSV Parser (Free)',
  excel_adapter: 'Excel Parser (Free)',
  ofx_parser: 'OFX Parser (Free)',
  claude_ocr: 'Claude OCR (~$0.04)',
  textract: 'AWS Textract (~$0.05)',
};

interface TierStats {
  count: number;
  cost: number;
}

interface DailyData {
  date: string;
  count: number;
  cost: number;
}

interface CaseExtractionData {
  case_id: string;
  client_name: string;
  total_files: number;
  total_cost: number;
  methods_used: Record<string, number>;
  problem_files: number;
  success_rate: number;
}

interface ExtractionAnalyticsData {
  total_uploads: number;
  total_cost_usd: number;
  cost_by_tier: Record<string, TierStats>;
  cost_by_day: DailyData[];
  avg_cost_per_upload: number;
  free_tier_percentage: number;
  paid_tier_percentage: number;
  period_days: number;
  by_case?: CaseExtractionData[];
  file_type_breakdown?: Record<string, { count: number; avg_cost: number; problem_rate: number }>;
}

interface ExtractionAnalyticsProps {
  days?: number;
}

export default function ExtractionAnalytics({ days = 30 }: ExtractionAnalyticsProps) {
  const [data, setData] = useState<ExtractionAnalyticsData | null>(null);
  const [caseData, setCaseData] = useState<CaseExtractionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCaseBreakdown, setShowCaseBreakdown] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(days);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [analyticsResponse, caseResponse] = await Promise.all([
        api.get(`/api/v1/upload-savefirst/analytics/extraction-costs?days=${selectedPeriod}`),
        api.get(`/api/v1/upload-savefirst/analytics/extraction-by-case?days=${selectedPeriod}`).catch(() => ({ cases: [] }))
      ]);
      setData(analyticsResponse);
      setCaseData(caseResponse.cases || []);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load extraction analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedPeriod]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8">
        <div className="text-center text-gray-500 py-8">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
          <p className="font-medium">Unable to load extraction analytics</p>
          <p className="text-sm mt-2">{error}</p>
          <button 
            onClick={fetchData}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Prepare pie chart data
  const pieData = Object.entries(data.cost_by_tier)
    .filter(([_, stats]) => stats.count > 0)
    .map(([tier, stats]) => ({
      name: TIER_LABELS[tier] || tier,
      value: stats.count,
      cost: stats.cost,
      color: TIER_COLORS[tier] || TIER_COLORS.unknown,
    }))
    .sort((a, b) => b.value - a.value);

  // Prepare daily trend data
  const dailyTrendData = data.cost_by_day.map(d => ({
    ...d,
    dateLabel: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
  }));

  // Calculate problem files (files that needed paid tiers)
  const problemFiles = (data.cost_by_tier.claude_ocr?.count || 0) + (data.cost_by_tier.textract?.count || 0);
  const problemRate = data.total_uploads > 0 ? (problemFiles / data.total_uploads) * 100 : 0;

  return (
    <div className="space-y-6" data-testid="extraction-analytics">
      {/* Header with Period Selector */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Database className="w-6 h-6 text-blue-600" />
            Extraction Pipeline Analytics
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Document processing costs, methods, and performance
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
          <button
            onClick={fetchData}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <RefreshCw className="w-4 h-4 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
          <div className="flex items-center gap-2 text-blue-700 mb-2">
            <FileText className="w-5 h-5" />
            <span className="text-sm font-medium">Total Files</span>
          </div>
          <p className="text-3xl font-bold text-blue-900">{data.total_uploads.toLocaleString()}</p>
          <p className="text-xs text-blue-600 mt-1">Last {selectedPeriod} days</p>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
          <div className="flex items-center gap-2 text-green-700 mb-2">
            <DollarSign className="w-5 h-5" />
            <span className="text-sm font-medium">Total Cost</span>
          </div>
          <p className="text-3xl font-bold text-green-900">${data.total_cost_usd.toFixed(2)}</p>
          <p className="text-xs text-green-600 mt-1">${data.avg_cost_per_upload.toFixed(4)}/file avg</p>
        </div>

        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 border border-emerald-200">
          <div className="flex items-center gap-2 text-emerald-700 mb-2">
            <Zap className="w-5 h-5" />
            <span className="text-sm font-medium">Free Processing</span>
          </div>
          <p className="text-3xl font-bold text-emerald-900">{data.free_tier_percentage.toFixed(1)}%</p>
          <p className="text-xs text-emerald-600 mt-1">Local OCR success rate</p>
        </div>

        <div className={`bg-gradient-to-br rounded-xl p-4 border ${
          problemRate > 20 ? 'from-red-50 to-red-100 border-red-200' : 
          problemRate > 10 ? 'from-amber-50 to-amber-100 border-amber-200' : 
          'from-purple-50 to-purple-100 border-purple-200'
        }`}>
          <div className={`flex items-center gap-2 mb-2 ${
            problemRate > 20 ? 'text-red-700' : problemRate > 10 ? 'text-amber-700' : 'text-purple-700'
          }`}>
            <Cloud className="w-5 h-5" />
            <span className="text-sm font-medium">Paid API Usage</span>
          </div>
          <p className={`text-3xl font-bold ${
            problemRate > 20 ? 'text-red-900' : problemRate > 10 ? 'text-amber-900' : 'text-purple-900'
          }`}>{problemRate.toFixed(1)}%</p>
          <p className={`text-xs mt-1 ${
            problemRate > 20 ? 'text-red-600' : problemRate > 10 ? 'text-amber-600' : 'text-purple-600'
          }`}>{problemFiles} files needed paid OCR</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Trends Chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Extraction Volume & Cost</h3>
          {dailyTrendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={dailyTrendData}>
                <defs>
                  <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorCost" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="dateLabel" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v.toFixed(2)}`} />
                <Tooltip 
                  formatter={(value: number, name: string) => {
                    // name is the display name from the Legend (e.g., "Files Processed", "Daily Cost")
                    if (name === 'Files Processed' || name.toLowerCase().includes('file')) {
                      return [`${value} files`, 'Files Processed'];
                    } else {
                      return [`$${value.toFixed(3)}`, 'Daily Cost'];
                    }
                  }}
                />
                <Legend />
                <Area yAxisId="left" type="monotone" dataKey="count" stroke="#3b82f6" fill="url(#colorCount)" name="Files Processed" />
                <Line yAxisId="right" type="monotone" dataKey="cost" stroke="#22c55e" strokeWidth={2} dot={false} name="Daily Cost" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No data available for selected period
            </div>
          )}
        </div>

        {/* Extraction Method Distribution */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Extraction Method Distribution</h3>
          {pieData.length > 0 ? (
            <div className="flex">
              <ResponsiveContainer width="50%" height={280}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => [`${value} files`, 'Count']} />
                </PieChart>
              </ResponsiveContainer>
              <div className="w-1/2 pl-4 space-y-2 overflow-y-auto max-h-[280px]">
                {pieData.map((tier, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tier.color }} />
                      <span className="text-gray-700 truncate max-w-[140px]">{tier.name}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-medium text-gray-900">{tier.value}</span>
                      {tier.cost > 0 && (
                        <span className="text-gray-500 ml-1">(${tier.cost.toFixed(2)})</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No extraction data available
            </div>
          )}
        </div>
      </div>

      {/* Tier Details Table */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Extraction Tier Performance</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-600">Tier</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">Files</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">Cost</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">Avg Cost</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">% of Total</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.cost_by_tier)
                .sort(([, a], [, b]) => b.count - a.count)
                .map(([tier, stats]) => {
                  const pct = data.total_uploads > 0 ? (stats.count / data.total_uploads) * 100 : 0;
                  const avgCost = stats.count > 0 ? stats.cost / stats.count : 0;
                  const isFree = avgCost === 0;
                  
                  return (
                    <tr key={tier} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: TIER_COLORS[tier] || TIER_COLORS.unknown }}
                          />
                          <span className="font-medium">{TIER_LABELS[tier] || tier}</span>
                        </div>
                      </td>
                      <td className="text-right py-3 px-4 font-mono">{stats.count.toLocaleString()}</td>
                      <td className="text-right py-3 px-4 font-mono">
                        {isFree ? (
                          <span className="text-green-600">Free</span>
                        ) : (
                          <span className="text-gray-900">${stats.cost.toFixed(2)}</span>
                        )}
                      </td>
                      <td className="text-right py-3 px-4 font-mono text-gray-600">
                        {isFree ? '-' : `$${avgCost.toFixed(4)}`}
                      </td>
                      <td className="text-right py-3 px-4">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full"
                              style={{ 
                                width: `${pct}%`,
                                backgroundColor: TIER_COLORS[tier] || TIER_COLORS.unknown
                              }}
                            />
                          </div>
                          <span className="text-gray-600 w-12 text-right">{pct.toFixed(1)}%</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {isFree ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                            <Zap className="w-3 h-3" /> Local
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                            <Cloud className="w-3 h-3" /> API
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Case-Level Breakdown (Expandable) */}
      {caseData.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200">
          <button
            onClick={() => setShowCaseBreakdown(!showCaseBreakdown)}
            className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition"
          >
            <div className="flex items-center gap-3">
              <Eye className="w-5 h-5 text-gray-600" />
              <span className="text-lg font-semibold text-gray-900">Case-Level Breakdown</span>
              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-sm rounded-full">
                {caseData.length} cases
              </span>
            </div>
            {showCaseBreakdown ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          
          {showCaseBreakdown && (
            <div className="border-t border-gray-200 p-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Case</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-600">Files</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-600">Cost</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-600">Success Rate</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Primary Method</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-600">Problem Files</th>
                    </tr>
                  </thead>
                  <tbody>
                    {caseData
                      .sort((a, b) => b.total_cost - a.total_cost)
                      .slice(0, 20)
                      .map((caseItem) => {
                        const primaryMethod = Object.entries(caseItem.methods_used || {})
                          .sort(([, a], [, b]) => b - a)[0];
                        
                        return (
                          <tr key={caseItem.case_id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4">
                              <div>
                                <p className="font-medium text-gray-900">{caseItem.client_name || 'Unknown'}</p>
                                <p className="text-xs text-gray-500">{caseItem.case_id.slice(0, 8)}...</p>
                              </div>
                            </td>
                            <td className="text-right py-3 px-4 font-mono">{caseItem.total_files}</td>
                            <td className="text-right py-3 px-4 font-mono">
                              ${caseItem.total_cost.toFixed(2)}
                            </td>
                            <td className="text-right py-3 px-4">
                              <span className={`font-medium ${
                                caseItem.success_rate >= 90 ? 'text-green-600' :
                                caseItem.success_rate >= 70 ? 'text-amber-600' : 'text-red-600'
                              }`}>
                                {caseItem.success_rate.toFixed(0)}%
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              {primaryMethod && (
                                <span 
                                  className="px-2 py-1 rounded-full text-xs"
                                  style={{ 
                                    backgroundColor: `${TIER_COLORS[primaryMethod[0]] || TIER_COLORS.unknown}20`,
                                    color: TIER_COLORS[primaryMethod[0]] || TIER_COLORS.unknown
                                  }}
                                >
                                  {primaryMethod[0]}
                                </span>
                              )}
                            </td>
                            <td className="text-right py-3 px-4">
                              {caseItem.problem_files > 0 ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">
                                  <AlertTriangle className="w-3 h-3" />
                                  {caseItem.problem_files}
                                </span>
                              ) : (
                                <span className="text-green-600">✓</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Cost Savings Note */}
      {data.free_tier_percentage > 50 && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-green-800">Excellent Cost Efficiency!</h4>
              <p className="text-sm text-green-700 mt-1">
                {data.free_tier_percentage.toFixed(1)}% of documents processed using free local OCR.
                {data.total_uploads > 0 && data.paid_tier_percentage > 0 && (
                  <> Estimated savings: <strong>${((data.total_uploads * 0.04) - data.total_cost_usd).toFixed(2)}</strong> vs. all-Claude processing.</>
                )}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
